import type { EmailEngagementTrendPointDto } from '@/types/emailBlast'

interface Props {
  points: EmailEngagementTrendPointDto[]
}

// Geometry of the SVG canvas (matches the ORB-384 mock proportions).
const W = 600
const H = 200
const PAD_L = 24
const PAD_R = 12
const TOP = 12
const BASELINE = 180

/**
 * Daily sends / opens / clicks trend (ORB-384). Sends are faint context bars; opens and clicks are
 * lines with clicks emphasized (the trustworthy signal). All three share one linear scale so the lines
 * sit honestly beneath the send volume — no misleading dual axis. No reusable time-series chart exists
 * in the codebase, so this is purpose-built to the mock.
 */
export function EmailEngagementTrendChart({ points }: Props) {
  const n = points.length
  const max = Math.max(1, ...points.flatMap(p => [p.Sends, p.Opens, p.Clicks]))
  const plotW = W - PAD_L - PAD_R
  const plotH = BASELINE - TOP
  // With one point, center it; otherwise spread across the plot.
  const x = (i: number) => (n <= 1 ? PAD_L + plotW / 2 : PAD_L + (i / (n - 1)) * plotW)
  const y = (v: number) => BASELINE - (v / max) * plotH
  const barW = n <= 1 ? 24 : Math.max(4, Math.min(28, (plotW / n) * 0.5))

  const line = (key: 'Opens' | 'Clicks') => points.map((p, i) => `${x(i).toFixed(1)},${y(p[key]).toFixed(1)}`).join(' ')

  // Sparse x labels so they don't collide (first, last, and a few between).
  const labelEvery = Math.max(1, Math.ceil(n / 6))

  return (
    <svg viewBox={`0 0 ${W} ${H}`} class="w-full h-44" role="img" aria-label="Sends, opens and clicks per day">
      {/* gridlines */}
      <line x1={PAD_L} y1={BASELINE} x2={W - PAD_R} y2={BASELINE} stroke="currentColor" class="text-slate-200 dark:text-slate-800" stroke-width="1" />
      <line x1={PAD_L} y1={TOP + plotH / 2} x2={W - PAD_R} y2={TOP + plotH / 2} stroke="currentColor" class="text-slate-100 dark:text-slate-800/60" stroke-width="1" />

      {/* sends bars (faint context) */}
      <g class="text-slate-200 dark:text-slate-700" fill="currentColor">
        {points.map((p, i) => {
          const top = y(p.Sends)
          return <rect key={`s-${i}`} x={(x(i) - barW / 2).toFixed(1)} y={top.toFixed(1)} width={barW.toFixed(1)} height={Math.max(0, BASELINE - top).toFixed(1)} rx="2" />
        })}
      </g>

      {/* opens line */}
      {n > 1 && <polyline fill="none" stroke="currentColor" class="text-indigo-400" stroke-width="2" stroke-linejoin="round" points={line('Opens')} />}
      {/* clicks line (emphasized) */}
      {n > 1 && <polyline fill="none" stroke="currentColor" class="text-primary-600" stroke-width="2.5" stroke-linejoin="round" points={line('Clicks')} />}
      {/* clicks markers */}
      <g class="text-primary-600" fill="currentColor">
        {points.map((p, i) => <circle key={`c-${i}`} cx={x(i).toFixed(1)} cy={y(p.Clicks).toFixed(1)} r="2.5" />)}
      </g>
      {/* opens markers (single-point fallback so a one-day trend still shows) */}
      {n === 1 && <circle cx={x(0)} cy={y(points[0].Opens)} r="2.5" class="text-indigo-400" fill="currentColor" />}

      {/* x labels */}
      <g class="text-slate-400 dark:text-slate-500" fill="currentColor" style={{ fontSize: '10px' }}>
        {points.map((p, i) =>
          i % labelEvery === 0 || i === n - 1 ? (
            <text key={`x-${i}`} x={x(i).toFixed(1)} y={H - 4} text-anchor="middle">{shortDay(p.Date)}</text>
          ) : null,
        )}
      </g>
    </svg>
  )
}

function shortDay(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
